import React, { useCallback, useEffect, useRef, useState } from 'react';
import Badge from '@material-ui/core/Badge';
import IconButton from '@material-ui/core/IconButton';
import Popover from '@material-ui/core/Popover';
import Typography from '@material-ui/core/Typography';
import Divider from '@material-ui/core/Divider';
import NotificationsNoneIcon from '@material-ui/icons/NotificationsNone';
import NotificationsActiveIcon from '@material-ui/icons/NotificationsActive';
import AnnouncementIcon from '@material-ui/icons/Announcement';
import PersonAddIcon from '@material-ui/icons/PersonAdd';
import AssignmentIcon from '@material-ui/icons/Assignment';
import CheckCircleOutlineIcon from '@material-ui/icons/CheckCircleOutline';
import { makeStyles } from '@material-ui/core/styles';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { get, patch, search } from '../../utils/ajax';
import { localRoutes, remoteRoutes } from '../../data/constants';
import { IState } from '../../data/types';
import { isStudent, isTrainer, isHubManager } from '../../data/appRoles';
import { colors } from '../../theme/tokens';

const CORAL = colors.brand;

const useStyles = makeStyles(() => ({
  bell: { color: colors.iconMuted },
  bellActive: { color: CORAL },
  badge: {
    '& .MuiBadge-badge': {
      background: CORAL,
      color: colors.onBrand,
      fontSize: 10,
      fontWeight: 700,
      minWidth: 16,
      height: 16,
      padding: '0 4px',
    },
  },

  popover: {
    width: 340,
    display: 'flex',
    flexDirection: 'column',
    borderRadius: 12,
    overflow: 'hidden',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    padding: '14px 16px 10px',
    background: colors.bgSurface,
  },
  headerTitle: {
    fontSize: 14,
    fontWeight: 700,
    color: colors.textPrimary,
  },
  clearBtn: {
    fontSize: 11,
    color: CORAL,
    fontWeight: 600,
    cursor: 'pointer',
    border: 'none',
    background: 'none',
    padding: 0,
    '&:hover': { textDecoration: 'underline' },
  },

  list: {
    maxHeight: 216,
    overflowY: 'auto' as any,
    background: colors.bgSurface,
  },
  empty: {
    padding: '32px 16px',
    textAlign: 'center' as any,
    color: colors.textDisabled,
    fontSize: 13,
  },

  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: 12,
    padding: '12px 16px',
    cursor: 'pointer',
    borderBottom: `1px solid ${colors.hover}`,
    transition: 'background 0.15s',
    '&:last-child': { borderBottom: 'none' },
    '&:hover': { background: colors.bgSubtle },
  },
  itemUnread: { background: colors.brandTintSoft },
  iconBox: {
    width: 34,
    height: 34,
    borderRadius: 10,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
  },
  itemTitle: {
    fontSize: 13,
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: 2,
  },
  itemBody: {
    fontSize: 11,
    color: colors.iconMuted,
    lineHeight: 1.4,
    overflow: 'hidden',
    display: '-webkit-box' as any,
    '-webkit-line-clamp': 2,
    '-webkit-box-orient': 'vertical',
  },
  itemTime: { fontSize: 10, color: colors.textDisabled, marginTop: 3 },
  unreadDot: {
    width: 7,
    height: 7,
    borderRadius: '50%',
    background: CORAL,
    flexShrink: 0,
    marginTop: 5,
  },
}));

interface Notif {
  id: string;
  type: 'announcement' | 'enrollment' | 'submission' | 'grade' | 'assignment';
  title: string;
  body: string;
  time: string;
  read: boolean;
  // Set only for rows backed by a real notification row. Those carry their
  // read state server-side; the rest are derived client-side and fall back
  // to localStorage so dismissals at least survive a reload.
  serverId?: number;
  relatedType?: string | null;
  relatedId?: number | null;
}

const timeAgo = (dateStr: string): string => {
  if (!dateStr) return '';
  try {
    const diff = Date.now() - new Date(dateStr).getTime();
    const mins = Math.floor(diff / 60000);
    if (mins < 1) return 'just now';
    if (mins < 60) return `${mins}m ago`;
    const hrs = Math.floor(mins / 60);
    if (hrs < 24) return `${hrs}h ago`;
    return `${Math.floor(hrs / 24)}d ago`;
  } catch {
    return '';
  }
};

const TYPE_META: Record<
  Notif['type'],
  { icon: React.ElementType; color: string; bg: string }
> = {
  announcement: {
    icon: AnnouncementIcon,
    color: CORAL,
    bg: colors.brandTint,
  },
  enrollment: {
    icon: PersonAddIcon,
    color: colors.accentBlue,
    bg: colors.tintBlue,
  },
  submission: {
    icon: AssignmentIcon,
    color: colors.accentAmber,
    bg: colors.tintAmber,
  },
  grade: {
    icon: CheckCircleOutlineIcon,
    color: colors.accentGreen,
    bg: colors.tintGreen,
  },
  assignment: {
    icon: AssignmentIcon,
    color: colors.accentPurple,
    bg: colors.tintPurple,
  },
};

const POLL_MS = 60_000; // refresh every 60 s
const READ_KEY = '__elevate__notifs_read';

// Only the derived rows need this — server-backed ones persist their own
// read state. Kept small by dropping anything no longer in the feed.
const readDerived = (): Set<string> => {
  try {
    return new Set(JSON.parse(localStorage.getItem(READ_KEY) || '[]'));
  } catch {
    return new Set();
  }
};

const NotificationBell: React.FC = () => {
  const classes = useStyles();
  const history = useHistory();
  const user = useSelector((state: IState) => state.core.user);
  const student = isStudent(user);
  const trainer = isTrainer(user);
  const hubManager = isHubManager(user);
  const isAdmin = !student && !trainer; // admin / hub manager / super admin

  const [notifs, setNotifs] = useState<Notif[]>([]);
  const [anchor, setAnchor] = useState<HTMLElement | null>(null);
  const [readIds, setReadIds] = useState<Set<string>>(readDerived);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const isRead = useCallback(
    (n: Notif) => (n.serverId ? n.read : readIds.has(n.id)),
    [readIds],
  );
  const unread = notifs.filter((n) => !isRead(n)).length;

  const markDerivedRead = (ids: string[]) => {
    setReadIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.add(id));
      try {
        localStorage.setItem(READ_KEY, JSON.stringify(Array.from(next)));
      } catch {}
      return next;
    });
  };

  const buildNotifs = useCallback(() => {
    const next: Notif[] = [];

    // ── Real per-user notifications (server-backed read state) ─────
    get(
      remoteRoutes.notifications,
      (data: any) => {
        const list: any[] = Array.isArray(data) ? data : data?.data || [];
        const real: Notif[] = list.slice(0, 10).map((n: any) => ({
          id: `srv-${n.id}`,
          serverId: n.id,
          type: n.type === 'assignment' ? 'assignment' : 'announcement',
          title: n.title || 'Notification',
          body: n.message || '',
          time: timeAgo(n.createdAt),
          read: !!n.isRead,
          relatedType: n.relatedType,
          relatedId: n.relatedId,
        }));
        setNotifs((prev) => mergeServerNotifs(prev, real));
      },
      undefined,
      undefined,
    );

    // Announcements are not fetched separately any more — the notification
    // table above already carries one row per user per announcement, with
    // real read state. Fetching them again only produced duplicates that
    // could never be dismissed.

    // ── Pending enrollments (admin / hub manager) ──────────────────
    if (isAdmin || hubManager) {
      get(
        remoteRoutes.enrollmentPending,
        (data: any) => {
          const list: any[] = Array.isArray(data) ? data : data?.data || [];
          const pending: Notif[] = list.slice(0, 5).map((e: any) => ({
            id: `enr-${e.id}`,
            type: 'enrollment' as const,
            title: 'New Enrollment Request',
            body: `${
              e.studentName || e.name || 'A student'
            } wants to enroll in ${e.courseName || 'a course'}`,
            time: timeAgo(e.createdAt || e.requestedAt),
            read: false,
          }));
          setNotifs((prev) => mergeNotifs(prev, pending, 'enrollment'));
        },
        undefined,
        undefined,
      );
    }

    // ── Ungraded submissions (admin / trainer) ─────────────────────
    if (isAdmin || trainer) {
      const params = trainer
        ? { instructorId: user?.contactId ?? user?.id, limit: 5 }
        : { limit: 5 };
      search(
        remoteRoutes.assignmentSubmissions,
        params,
        (data: any) => {
          const list: any[] = Array.isArray(data) ? data : data?.data || [];
          const ungraded = list
            .filter(
              (s: any) =>
                !s.grade && (s.status || '').toLowerCase() !== 'graded',
            )
            .slice(0, 5)
            .map((s: any) => ({
              id: `sub-${s.id}`,
              type: 'submission' as const,
              title: 'Assignment Submission',
              body: `${s.studentName || s.name || 'A student'} submitted "${
                s.assignmentTitle || s.projectTitle || 'an assignment'
              }"`,
              time: timeAgo(s.submittedAt || s.createdAt),
              read: false,
            }));
          setNotifs((prev) => mergeNotifs(prev, ungraded, 'submission'));
        },
        undefined,
        undefined,
      );
    }
  }, [isAdmin, hubManager, trainer, user?.contactId, user?.id]);

  useEffect(() => {
    buildNotifs();
    timerRef.current = setInterval(buildNotifs, POLL_MS);
    return () => {
      if (timerRef.current) clearInterval(timerRef.current);
    };
  }, [buildNotifs]);

  const open = (e: React.MouseEvent<HTMLElement>) => {
    setAnchor(e.currentTarget);
  };

  // Closing no longer silently marks everything read — that was why items
  // appeared to be dismissed and then came straight back on reload.
  const close = () => setAnchor(null);

  const clearAll = () => {
    patch(`${remoteRoutes.notifications}/read-all`, {}, () => undefined);
    setNotifs((prev) =>
      prev.map((n) => (n.serverId ? { ...n, read: true } : n)),
    );
    markDerivedRead(notifs.filter((n) => !n.serverId).map((n) => n.id));
  };

  const targetFor = (n: Notif): string | null => {
    // Submissions are reviewed on different screens depending on who's
    // looking: a trainer only sees their own courses, an admin reviews
    // across every hub/course. Sending an admin to the trainer-scoped
    // screen left them on an empty list (it filters by the viewer's own
    // instructor id, which an admin doesn't have).
    if (n.type === 'enrollment') return localRoutes.students;
    if (n.type === 'submission')
      return isAdmin ? localRoutes.adminGrades : localRoutes.trainerSubmissions;
    if (n.type === 'assignment' || n.relatedType === 'assignment') {
      if (student) return localRoutes.myAssessments;
      return isAdmin ? localRoutes.adminGrades : localRoutes.trainerSubmissions;
    }
    return student ? localRoutes.myCourses : localRoutes.dashboard;
  };

  const handleClick = (n: Notif) => {
    if (n.serverId) {
      patch(
        `${remoteRoutes.notifications}/${n.serverId}/read`,
        {},
        () => undefined,
      );
      setNotifs((prev) =>
        prev.map((it) => (it.id === n.id ? { ...it, read: true } : it)),
      );
    } else {
      markDerivedRead([n.id]);
    }
    const to = targetFor(n);
    setAnchor(null);
    if (to) history.push(to);
  };

  return (
    <>
      <IconButton size="small" onClick={open} style={{ marginRight: 4 }}>
        <Badge badgeContent={unread || 0} className={classes.badge} max={9}>
          {unread > 0 ? (
            <NotificationsActiveIcon
              style={{ fontSize: 22 }}
              className={classes.bellActive}
            />
          ) : (
            <NotificationsNoneIcon
              style={{ fontSize: 22 }}
              className={classes.bell}
            />
          )}
        </Badge>
      </IconButton>

      <Popover
        open={Boolean(anchor)}
        anchorEl={anchor}
        onClose={close}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
        PaperProps={{ className: classes.popover }}
      >
        {/* Header */}
        <div className={classes.header}>
          <Typography className={classes.headerTitle}>
            Notifications {unread > 0 && `(${unread})`}
          </Typography>
          {unread > 0 && (
            <button className={classes.clearBtn} onClick={clearAll}>
              Mark all read
            </button>
          )}
        </div>
        <Divider />

        {/* List */}
        <div className={classes.list}>
          {notifs.length === 0 ? (
            <div className={classes.empty}>
              <NotificationsNoneIcon
                style={{ fontSize: 32, marginBottom: 8, opacity: 0.4 }}
              />
              <br />
              No notifications yet
            </div>
          ) : (
            notifs.map((n) => {
              const meta = TYPE_META[n.type];
              const Icon = meta.icon;
              const isUnread = !isRead(n);
              return (
                <div
                  key={n.id}
                  role="button"
                  tabIndex={0}
                  onClick={() => handleClick(n)}
                  onKeyPress={() => handleClick(n)}
                  className={`${classes.item} ${
                    isUnread ? classes.itemUnread : ''
                  }`}
                  style={{ cursor: 'pointer' }}
                >
                  <div
                    className={classes.iconBox}
                    style={{ background: meta.bg }}
                  >
                    <Icon style={{ fontSize: 17, color: meta.color }} />
                  </div>
                  <div style={{ flex: 1, minWidth: 0 }}>
                    <div className={classes.itemTitle}>{n.title}</div>
                    <div className={classes.itemBody}>{n.body}</div>
                    {n.time && <div className={classes.itemTime}>{n.time}</div>}
                  </div>
                  {isUnread && <div className={classes.unreadDot} />}
                </div>
              );
            })
          )}
        </div>
      </Popover>
    </>
  );
};

// Replace only the derived rows of a given type — server-backed rows are
// owned by mergeServerNotifs and must survive this.
function mergeNotifs(
  prev: Notif[],
  incoming: Notif[],
  type: Notif['type'],
): Notif[] {
  const kept = prev.filter((n) => n.serverId || n.type !== type);
  return [...incoming, ...kept].slice(0, 20);
}

// Replace every server-backed row, leaving derived ones untouched.
function mergeServerNotifs(prev: Notif[], incoming: Notif[]): Notif[] {
  const kept = prev.filter((n) => !n.serverId);
  return [...incoming, ...kept].slice(0, 20);
}

export default NotificationBell;
